import * as React from "react";

import { cva } from "class-variance-authority";

import { cn } from "@/utils";

import type { VariantProps } from "class-variance-authority";

const inputVariants = cva(
  "w-full h-[50px] rounded-md bg-fog text-garnet " +
    "placeholder-transparent " +
    "disabled:bg-fog-dark group-disabled/lock:bg-fog-dark " +
    "outline-none transition-colors",
  {
    variants: {
      size: {
        xs: "text-[12px] px-2",
        sm: "text-[14px] px-3",
        md: "text-[16px] px-3",
        lg: "text-[18px] px-4",
      },
      state: {
        default: "",
        error: "border-red-500 focus:border-red-500",
        success: "border-green-600 focus:border-green-600",
      },
    },
    defaultVariants: {
      size: "md",
      state: "default",
    },
  },
);
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    Omit<VariantProps<typeof inputVariants>, "size"> {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string | null) => void;

  variantSize?: "sm" | "md" | "lg" | "xs";
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  label?: string;
  hint?: string;
  state?: "default" | "error" | "success";
}

const Input = React.forwardRef(function Input(
  {
    className,
    startAdornment,
    endAdornment,
    label,
    hint,
    variantSize,
    state = "default",
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    onValueChange,
    ...props
  }: InputProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const innerRef = React.useRef<HTMLInputElement | null>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  const [currentValue, setCurrentValue] = React.useState<string>(
    (value ?? defaultValue ?? "").toString(),
  );
  const [focused, setFocused] = React.useState(false);

  // Update currentValue if value changes (controlled)
  React.useEffect(() => {
    if (value !== undefined) setCurrentValue((value as any)?.toString() ?? "");
  }, [value]);

  // On mount and when defaultValue changes, sync currentValue from DOM (for defaultValue)
  React.useEffect(() => {
    if (value === undefined && innerRef.current) {
      setCurrentValue(innerRef.current.value);
    }
  }, [defaultValue]);

  // Autofill detection: check input value after mount and on focus
  React.useEffect(() => {
    if (value !== undefined) return; // skip for controlled
    const checkAutofill = () => {
      if (innerRef.current) {
        const v = innerRef.current.value;
        if (v !== currentValue) setCurrentValue(v);
      }
    };
    // Check on mount
    checkAutofill();
    // Check again after a short delay (for autofill)
    const timeout = setTimeout(checkAutofill, 300);
    // Listen for focus events
    const input = innerRef.current;
    input?.addEventListener("focus", checkAutofill);
    return () => {
      clearTimeout(timeout);
      input?.removeEventListener("focus", checkAutofill);
    };
  }, [currentValue, value]);

  const hasText = currentValue.trim().length > 0;
  const floated = focused || hasText;

  return (
    <label className="relative block w-full">
      <div
        className={cn(
          "rounded-md p-[1px] transition-colors",
          state === "error"
            ? "ring-1 ring-red-500"
            : "bg-transparent focus-within:bg-gradient-deep-blue",
        )}
      >
        <div className="relative">
          {label && (
            <span
              className={cn(
                "absolute left-3 pointer-events-none transition-all duration-150 will-change-transform",
                floated
                  ? "top-[6px] body-xs"
                  : "top-1/2 -translate-y-1/2 body-small",
                !!startAdornment && 'left-7'
              )}
            >
              {label}
            </span>
          )}

          {startAdornment && (
            <span className={cn("pointer-events-none absolute left-3 inline-flex items-center text-garnet/70",
                floated && 'bottom-1',
                !floated && 'bottom-3'
              )}>
              {startAdornment}
            </span>
          )}
          {endAdornment && (
            <span className={cn("absolute right-2 inline-flex items-center",
                floated && 'bottom-1',
                !floated && 'bottom-3'
              )}>
              {endAdornment}
            </span>
          )}

          <input
            ref={innerRef}
            value={value}
            defaultValue={defaultValue}
            className={cn(
              inputVariants({ size: variantSize, state }),
              "pt-[28px] pb-[10px]", // space for floated label
              !!startAdornment && "pl-9",
              !!endAdornment && "pr-9",
              focused && state === "default" && "border-transparent",
              className,
            )}
            {...props}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            onChange={(e) => {
              setCurrentValue(e.currentTarget.value);
              onChange?.(e);
              onValueChange?.(e.currentTarget.value);
              onValueChange?.(e.currentTarget.value);
            }}
          />
          {/* Custom placeholder for button input type */}
          {props.type === "button" && !value && props.placeholder && (
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-garnet/50 pointer-events-none select-none">
              {props.placeholder}
            </span>
          )}
        </div>
      </div>

      {hint && (
        <span
          className={cn(
            "mt-1 text-[11px]",
            state === "error" ? "text-red-500" : "text-garnet",
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
});

Input.displayName = "Input";
export default Input;
export { inputVariants };
